import React, { useState, useRef, useEffect } from "react"
import ReactDOM from "react-dom/client"

/*
 * If you want to use fast-xml-parser instead of DOMParser, 
 * install it with: npm install fast-xml-parser --save
 * Then uncomment the line below:
 */
// import { XMLParser } from "fast-xml-parser";

// Import the XMLParser from fast-xml-parser
import { XMLParser } from 'fast-xml-parser';

// Use React from global scope if available (from WordPress), otherwise use imports
// This prevents conflicts with other React plugins
const PimReact = window.React || React;
const PimReactDOM = window.ReactDOM || ReactDOM;

// Only set globals if they're completely missing and we have local imports
if (!window.React && React) {
  window.React = React;
}
if (!window.ReactDOM && ReactDOM) {
  window.ReactDOM = ReactDOM;
}

const divsToUpdate = document.querySelectorAll(".pim-react-mount-target")

console.log('Found divs to update:', divsToUpdate.length);

divsToUpdate.forEach(async div => {
  try {
    console.log('Initializing div:', div);
    div.style.width = '100%'
    div.style.maxWidth = '100%'
    
    const preContent = div.querySelector("pre")?.innerText;
    console.log('Pre content found:', !!preContent);
    
    let data;
    
    try {
      // First try to parse normally
      data = preContent ? JSON.parse(preContent) : {};
      console.log('Successfully parsed JSON data:', data);
    } catch (e) {
      console.log('Initial JSON parse failed, attempting to sanitize');
      
      // Try to clean the JSON string before parsing
      const cleanedContent = preContent
        // Remove invalid escape characters
        .replace(/\\([^"\/bfnrtu])/g, '$1')
        // Fix any truncated escape sequences
        .replace(/\\$/g, '')
        // Remove any invalid UTF-8 sequences
        .replace(/[\u0000-\u0019]+/g, '')
        // Ensure proper escaping of forward slashes
        .replace(/\//g, '\\/');
        
      try {
        data = JSON.parse(cleanedContent);
        console.log('Successfully parsed cleaned JSON data:', data);
      } catch (secondError) {
        console.error('Failed to parse JSON even after cleaning:', secondError);
        // Provide fallback data structure
        data = {
          xmlData: null,
          pluginUrl: div.dataset.pluginUrl || ''
        };
      }
    }
    
    // Ensure we have a valid data object
    if (!data || typeof data !== 'object') {
      console.log('Creating default data object');
      data = {
        xmlData: null,
        pluginUrl: div.dataset.pluginUrl || ''
      };
    }
    
    data.xmlData = null; // We'll fetch from API by default
    
    // Check if createRoot is available (React 18+), otherwise fall back to render
    if (PimReactDOM.createRoot) {
      const root = PimReactDOM.createRoot(div);
      root.render(<OurComponent {...data} />);
    } else if (PimReactDOM.render) {
      // Fallback for older React versions
      PimReactDOM.render(<OurComponent {...data} />, div);
    } else {
      console.error('PIM Plugin: Neither createRoot nor render method available on ReactDOM');
      div.innerHTML = `
        <div style="color: #666; padding: 20px; text-align: center;">
          React is not properly loaded. Please refresh the page.
        </div>
      `;
      return;
    }
    
    div.classList.remove("pim-react-mount-target");
    
  } catch (error) {
    console.error('PIM Plugin - Error initializing component:', error);
    console.error('PIM Plugin - Available React:', !!PimReact);
    console.error('PIM Plugin - Available ReactDOM:', !!PimReactDOM);
    console.error('PIM Plugin - ReactDOM.createRoot:', !!PimReactDOM?.createRoot);
    console.error('PIM Plugin - ReactDOM.render:', !!PimReactDOM?.render);
    
    // Add visual feedback for users
    div.innerHTML = `
      <div style="color: #666; padding: 20px; text-align: center;">
        Error loading ISTQB plugin component.
        <br>
        <small>Please check browser console for details and refresh the page.</small>
      </div>
    `;
  }
});

function parseXMLData(xmlString) {
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(xmlString, "text/xml")
  
  const providerNodes = xmlDoc.getElementsByTagName("a:TrainingProviderWrapper")
  const providers = Array.from(providerNodes).map(provider => ({
    name: provider.getElementsByTagName("a:Name")[0]?.textContent || "",
    country: provider.getElementsByTagName("a:Country")[0]?.getElementsByTagName("a:LongName")[0]?.textContent || "",
    email: (provider.getElementsByTagName("a:Email")[0]?.textContent || "").split(/\s+or\s+/)[0],
    website: provider.getElementsByTagName("a:WebSite")[0]?.textContent || "",
    address: provider.getElementsByTagName("a:Address")[0]?.textContent || "",
    city: provider.getElementsByTagName("a:City")[0]?.textContent || "",
    phone: provider.getElementsByTagName("a:Phone")[0]?.textContent || "",
    referencePerson: provider.getElementsByTagName("a:ReferencePerson")[0]?.textContent || "",
    accreditatedBoard: {
      nameBoard: provider.getElementsByTagName("a:AccreditatedBoard")[0]?.getElementsByTagName("a:NameBoard")[0]?.textContent || ""
    },
    courses: Array.from(provider.getElementsByTagName("a:TrainingProviderCourseWrapper")).map(course => ({
      name: course.getElementsByTagName("a:CertificationType")[0]?.getElementsByTagName("a:Name")[0]?.textContent || "",
      id: course.getElementsByTagName("a:Id")[0]?.textContent || "",
      type: course.getElementsByTagName("a:MaterialType")[0]?.textContent || "",
      languages: Array.from(course.getElementsByTagName("a:LanguageWrapper")).map(lang => 
        lang.getElementsByTagName("a:NameLanguage")[0]?.textContent || ""
      )
    }))
  }))

  return {
    providers: providers,
    certifications: providers[0]?.courses || []
  }
}

function truncateText(text, wordLimit = 6, charLimit = 30) {
  // First check character length
  if (text.length > charLimit) {
    return text.substring(0, charLimit) + '...'
  }
  
  // Then check word count
  const words = text.split(' ')
  if (words.length > wordLimit) {
    return words.slice(0, wordLimit).join(' ') + '...'
  }
  
  return text
}

const ClearButton = ({ onClick, show }) => {
  if (!show) return null;
  
  return (
    <button 
      onClick={onClick}
      className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 z-20"
      title="Clear selection"
      type="button"
    >
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        className="h-5 w-5" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={2} 
          d="M6 18L18 6M6 6l12 12" 
        />
      </svg>
    </button>
  );
};

const Loader = () => (
  <div className="flex justify-center items-center p-4">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

const DropdownLoader = () => (
  <div className="px-4 py-2 text-gray-500 text-sm">
    <div className="flex items-center space-x-2">
      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
      <span>Loading...</span>
    </div>
  </div>
);

const isDevelopment = () => {
  return window.location.href.includes('http://devbench.local/members/');
};

function sanitizeXmlString(xmlString) {
    if (!xmlString) return null;
    
    // Remove any BOM and non-printable characters
    xmlString = xmlString.replace(/^\uFEFF/, '');
    xmlString = xmlString.replace(/[^\x09\x0A\x0D\x20-\uD7FF\uE000-\uFFFF]/g, '');
    
    // Ensure proper XML declaration
    if (!xmlString.match(/<\?xml[^>]+>\s*/)) {
        xmlString = '<?xml version="1.0" encoding="UTF-8"?>' + xmlString;
    }
    
    // Fix common encoding issues
    xmlString = xmlString.replace(/&(?!#?[a-zA-Z0-9]+;)/g, '&amp;');
    
    return xmlString;
}

// Add fallback data for common member boards
const FALLBACK_COUNTRIES = {
  "ANZTB": "Australia,New Zealand",
  "ATB": "Austria",
  "SSTB": "Sweden",
  "CSTB": "China",
  "DSTB": "Denmark",
  "EFTB": "Ethiopia",
  "FSTQB": "Finland",
  "GTB": "Germany",
  "HASTQB": "Hungary",
  "ITA-STQB": "Italy",
  "JSTQB": "Japan",
  "KSTQB": "Korea",
  "NSTB": "Netherlands",
  "PSTB": "Poland",
  "RTQB": "Romania",
  "RSTQB": "Russia",
  "SASTQB": "South Africa",
  "SEETB": "Bulgaria,Greece,Romania,Serbia,Turkey",
  "SSTB": "Spain",
  "SSTQB": "Switzerland",
  "TCSTQB": "Taiwan",
  "TQLA": "United States,Canada",
  "UKITB": "United Kingdom,Ireland"
};

// More robust extraction from raw XML
function extractAllCountriesOfOperation(rawXml) {
  if (!rawXml) return {};
  
  console.log('Extracting all CountriesOfOperation from raw XML');
  
  // Store extracted data by acronym, name and section index
  const extractedData = {};
  
  // Find all wrapper sections
  const wrapperRegex = /<(?:ns1:|a:)?(?:CertificationBoardWrapper|BoardMemberBoardWrapper|BoardExamProviderWrapper)>([\s\S]*?)<\/(?:ns1:|a:)?(?:CertificationBoardWrapper|BoardMemberBoardWrapper|BoardExamProviderWrapper)>/g;
  let wrapperMatch;
  let index = 0;
  
  while ((wrapperMatch = wrapperRegex.exec(rawXml)) !== null) {
    const section = wrapperMatch[1];
    index++;
    
    // Extract acronym
    const acronymMatch = section.match(/<(?:ns1:|a:)?Acronym>([^<]*)<\/(?:ns1:|a:)?Acronym>/);
    const acronym = acronymMatch ? acronymMatch[1].trim() : null;
    
    // Extract name - try different field names
    const nameMatches = [
      section.match(/<(?:ns1:|a:)?BoardName>([^<]*)<\/(?:ns1:|a:)?BoardName>/),
      section.match(/<(?:ns1:|a:)?Name>([^<]*)<\/(?:ns1:|a:)?Name>/),
      section.match(/<(?:ns1:|a:)?ExamProviderBorderName>([^<]*)<\/(?:ns1:|a:)?ExamProviderBorderName>/)
    ];
    
    const name = nameMatches.find(match => match !== null)?.[1]?.trim() || null;
    
    // Extract CountriesOfOperation
    const coMatch = section.match(/<(?:ns1:|a:)?CountriesOfOperation>([^<]*)<\/(?:ns1:|a:)?CountriesOfOperation>/);
    const countriesOfOperation = coMatch ? coMatch[1].trim() : null;
    
    // Store by acronym if available
    if (acronym && countriesOfOperation) {
      extractedData[acronym] = countriesOfOperation;
      console.log(`Found CountriesOfOperation for ${acronym}: ${countriesOfOperation}`);
    }
    
    // Store by name if available
    if (name && countriesOfOperation) {
      extractedData[name] = countriesOfOperation;
      console.log(`Found CountriesOfOperation for "${name}": ${countriesOfOperation}`);
    }
    
    // Store by index for fuzzy matching later
    if (countriesOfOperation) {
      extractedData[`__index_${index}`] = {
        acronym,
        name,
        countriesOfOperation
      };
    }
  }
  
  // Store in global object for debugging
  window.extractedCountriesData = extractedData;
  
  return extractedData;
}

// Modified fetchExamData to extract all countries
async function fetchExamData(pluginUrl, bypassCache = false) {
  try {
    console.log('Sending request for member board data using SOAP proxy');
    if (bypassCache) {
      console.log('🚫 BYPASS CACHE: Forcing fresh API request (development mode)');
    }
    
    const soapEnvelope = `<?xml version="1.0" encoding="UTF-8"?>
<soap:Envelope xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">
  <soap:Body>
    <GetMemberBoardCertificationsByBoard xmlns="http://tempuri.org/">
      <userId>istqb</userId>
      <pwd>osh7Vae4aiku5queevae</pwd>
    </GetMemberBoardCertificationsByBoard>
  </soap:Body>
</soap:Envelope>`;

    // Use the direct SOAP proxy instead of the REST API
    const soapProxyUrl = `${pluginUrl}/soap-proxy.php${bypassCache ? '?nocache=1' : ''}`;
    let response = await fetch(soapProxyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/xml;charset=UTF-8',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: soapEnvelope
    });

    console.log('Response status:', response.status);
    
    // If we get a 500 error, try fallback options
    if (response.status === 500) {
      console.log('Main SOAP proxy failed with 500, trying fallback options...');
      
      // First try the workaround script
      try {
        console.log('Trying workaround script...');
        const workaroundUrl = `${pluginUrl}/soap-workaround.php`;
        const workaroundResponse = await fetch(workaroundUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'text/xml;charset=UTF-8',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: soapEnvelope
        });

        console.log('Workaround response status:', workaroundResponse.status);
        
        if (workaroundResponse.ok) {
          console.log('✅ Workaround script succeeded!');
          const successHeader = workaroundResponse.headers.get('X-Workaround-Success');
          if (successHeader) {
            console.log('Successful approach:', successHeader);
          }
          response = workaroundResponse; // Use the workaround response
        } else {
          console.log('❌ Workaround script failed, trying Cloudflare bypass...');
          
          // Try the Cloudflare bypass script as a last resort
          try {
            const bypassUrl = `${pluginUrl}/cloudflare-bypass.php`;
            const bypassResponse = await fetch(bypassUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'text/xml;charset=UTF-8',
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: soapEnvelope
            });

            console.log('Cloudflare bypass response status:', bypassResponse.status);
            
            if (bypassResponse.ok) {
              console.log('✅ Cloudflare bypass succeeded!');
              response = bypassResponse; // Use the bypass response
            } else {
              console.log('❌ Cloudflare bypass also failed');
            }
          } catch (bypassError) {
            console.error('Cloudflare bypass error:', bypassError);
          }
        }
      } catch (workaroundError) {
        console.error('Workaround script error:', workaroundError);
        
        // Still try Cloudflare bypass even if workaround threw an error
        try {
          console.log('Trying Cloudflare bypass after workaround error...');
          const bypassUrl = `${pluginUrl}/cloudflare-bypass.php`;
          const bypassResponse = await fetch(bypassUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'text/xml;charset=UTF-8',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: soapEnvelope
          });

          console.log('Cloudflare bypass response status:', bypassResponse.status);
          
          if (bypassResponse.ok) {
            console.log('✅ Cloudflare bypass succeeded after workaround error!');
            response = bypassResponse;
          } else {
            console.log('❌ All fallback options failed');
          }
        } catch (bypassError) {
          console.error('Cloudflare bypass error:', bypassError);
          console.log('❌ All fallback options failed');
        }
      }
    }
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const rawResponse = await response.text();
    // Save the raw XML response for direct access if needed
    window.rawXmlResponse = rawResponse;
    
    // Extract all CountriesOfOperation data into a lookup table
    window.countriesOfOperationData = extractAllCountriesOfOperation(rawResponse);
    
    console.log('Raw response length:', rawResponse.length);

    // Parse the response
    const providers = extractProviderDataWithRegex(rawResponse);
    console.log('Successfully extracted providers:', providers.length);

    if (providers.length === 0) {
      console.warn('No providers found in response');
      return null;
    }

    return providers;
  } catch (error) {
    console.error('Error fetching member board data:', error);
    return null;
  }
}

// Updated fixCountriesOfOperation with improved lookup
function fixCountriesOfOperation(providers) {
  if (!Array.isArray(providers)) {
    return providers;
  }
  
  console.log('Starting to fix CountriesOfOperation for', providers.length, 'providers');
  
  // Get extraction data if available, or create empty object
  const extractedData = window.countriesOfOperationData || {};
  
  // Now apply values to providers
  return providers.map(provider => {
    // Get identifiers
    const acronym = provider.acronym || provider.Acronym;
    const name = provider.name || provider.boardName || provider.BoardName;
    
    // Try to find CountriesOfOperation from various sources
    let countriesOfOperation = null;
    
    // First try from extracted data
    if (acronym && extractedData[acronym]) {
      countriesOfOperation = extractedData[acronym];
      console.log(`Setting CountriesOfOperation for ${acronym} from extracted data`);
    } 
    else if (name && extractedData[name]) {
      countriesOfOperation = extractedData[name];
      console.log(`Setting CountriesOfOperation for "${name}" from extracted data`);
    }
    // Try fallback data
    else if (acronym && FALLBACK_COUNTRIES[acronym]) {
      countriesOfOperation = FALLBACK_COUNTRIES[acronym];
      console.log(`Setting CountriesOfOperation for ${acronym} from fallback data`);
    }
    // Use country or countryRegion as last resort
    else if (!provider.countriesOfOperation || provider.countriesOfOperation === '') {
      countriesOfOperation = provider.country || provider.countryRegion || '';
      if (countriesOfOperation) {
        console.log(`Using ${provider.country ? 'country' : 'countryRegion'} as CountriesOfOperation for ${acronym || name}`);
      }
    }
    
    // Update provider if we found a value
    if (countriesOfOperation && countriesOfOperation !== '') {
      provider.countriesOfOperation = countriesOfOperation;
    }
    
    return provider;
  });
}

function decodeUnicodeEscapes(str) {
  return str.replace(/\\u([a-fA-F0-9]{4})/g, (match, grp) =>
    String.fromCharCode(parseInt(grp, 16))
  );
}

// Function to use fast-xml-parser if available
function parseFastXML(xmlString) {
  // Check if fast-xml-parser is imported
  if (typeof XMLParser === 'undefined') {
    console.log('fast-xml-parser not available, using DOMParser instead');
    return null;
  }
  
  try {
    // Clean the XML string of any potential issues
    xmlString = xmlString
      .trim()
      .replace(/^["']|["']$/g, '') // Remove quotes
      .replace(/\\"/g, '"') // Unescape quotes
      .replace(/\\\//g, '/') // Unescape slashes
      .replace(/^\uFEFF/, '') // Remove BOM
      .replace(/[^\x09\x0A\x0D\x20-\uD7FF\uE000-\uFFFF]/g, '') // Remove invalid XML characters
      .replace(/&(?!(?:apos|quot|[gl]t|amp);|#)/g, '&amp;'); // Fix unescaped ampersands

    // Ensure proper XML declaration
    if (!xmlString.startsWith('<?xml')) {
      xmlString = '<?xml version="1.0" encoding="UTF-8"?>' + xmlString;
    }

    // Extract the XML content if possible
    const xmlMatch = xmlString.match(/<\?xml.*?<\/s:Envelope>/s);
    if (xmlMatch) {
      xmlString = xmlMatch[0];
      console.log('Successfully extracted XML content');
    } else {
      console.warn('Could not find complete XML document, trying alternative extraction');
      const startPos = xmlString.indexOf('<?xml');
      const endPos = xmlString.lastIndexOf('</s:Envelope>');
      if (startPos !== -1 && endPos !== -1) {
        xmlString = xmlString.substring(startPos, endPos + 12);
        console.log('Extracted XML using alternative method');
      }
    }
    
    // Parse XML using fast-xml-parser
    const options = {
      ignoreAttributes: false,
      attributeNamePrefix: "@_",
      allowBooleanAttributes: true,
      parseAttributeValue: true,
      parseTagValue: true,
      trimValues: true,
      isArray: (name, jpath, isLeafNode, isAttribute) => {
        // Make sure these tags are always treated as arrays
        const arrayTags = [
          'BoardExamProviderWrapper',
          'BoardExamProviderExamWrapper',
          'Languages'
        ];
        return arrayTags.includes(name);
      }
    };
    
    const parser = new XMLParser(options);
    const result = parser.parse(xmlString);
    
    console.log('Successfully parsed XML with fast-xml-parser');
    return result;
  } catch (error) {
    console.error('Error parsing XML with fast-xml-parser:', error);
    return null;
  }
}

// Enhanced XML parsing function using DOMParser
function parseXMLWithDOMParser(xmlString) {
  try {
    // Log first 200 characters for debugging
    console.log('XML string start:', xmlString.substring(0, 200).replace(/</g, '&lt;'));
    
    // Basic XML cleaning - only essential transformations
    let cleanedXml = xmlString
      .trim()
      .replace(/^["']|["']$/g, '') // Remove wrapping quotes
      .replace(/\\"/g, '"') // Unescape quotes
      .replace(/\\\//g, '/') // Unescape slashes
      .replace(/^\uFEFF/, ''); // Remove BOM
    
    // Try to extract SOAP envelope if present
    const envelopeStart = cleanedXml.indexOf('<s:Envelope') !== -1 ? 
                         cleanedXml.indexOf('<s:Envelope') :
                         cleanedXml.indexOf('<soap:Envelope');
    
    const envelopeEnd = cleanedXml.lastIndexOf('</s:Envelope>') !== -1 ?
                       cleanedXml.lastIndexOf('</s:Envelope>') + 12 :
                       cleanedXml.lastIndexOf('</soap:Envelope>') + 16;
    
    if (envelopeStart !== -1 && envelopeEnd !== -1 && envelopeEnd > envelopeStart) {
      console.log('Found SOAP envelope, extracting...');
      cleanedXml = cleanedXml.substring(envelopeStart, envelopeEnd);
      
      // Add XML declaration if missing
      if (!cleanedXml.startsWith('<?xml')) {
        cleanedXml = '<?xml version="1.0" encoding="UTF-8"?>\n' + cleanedXml;
      }
    }
    
    // Parse XML using DOMParser
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(cleanedXml, "text/xml");
    
    // Check for parsing errors
    const parserError = xmlDoc.getElementsByTagName('parsererror');
    if (parserError.length > 0) {
      console.error('XML parsing error:', parserError[0].textContent);
      throw new Error('XML parsing failed');
    }
    
    // Try to find the result element with namespace-aware selectors
    const nsResolver = xmlDoc.createNSResolver(xmlDoc.documentElement);
    const evaluator = new XPathEvaluator();
    
    // Try different possible result element paths
    const xpathQueries = [
      "//ns2:GetMemberBoardCertificationsByBoardResult",
      "//GetMemberBoardCertificationsByBoardResult",
      "//ns1:GetMemberBoardCertificationsByBoardResult",
      "//a:GetMemberBoardCertificationsByBoardResult",
      "//*[local-name()='GetMemberBoardCertificationsByBoardResult']"
    ];
    
    let resultNode = null;
    for (const query of xpathQueries) {
      try {
        resultNode = evaluator.evaluate(
          query,
          xmlDoc,
          nsResolver,
          XPathResult.FIRST_ORDERED_NODE_TYPE,
          null
        ).singleNodeValue;
        
        if (resultNode) {
          console.log('Found result node using query:', query);
          break;
        }
      } catch (e) {
        console.warn('XPath query failed:', query, e);
      }
    }
    
    if (!resultNode) {
      console.error('Could not find result element in XML');
      throw new Error('Result element not found');
    }
    
    return xmlDoc;
  } catch (error) {
    console.error('XML parsing failed:', error);
    return createFallbackXmlDoc(xmlString);
  }
}

// Function to extract data using regular expressions as a last resort
function createFallbackXmlDoc(xmlString) {
  console.log('Creating fallback XML document using regex extraction');
  try {
    // Create a basic XML document
    const xml = document.implementation.createDocument(null, "root", null);
    const root = xml.documentElement;
    
    // Clean XML string minimally
    const cleanXml = xmlString
      .trim()
      .replace(/\\"/g, '"')
      .replace(/\\\//g, '/');
    
    // Detect response type using more flexible patterns
    const hasMemberBoardTags = 
      /BoardMemberBoardWrapper|GetMemberBoardCertificationsByBoard/i.test(cleanXml);
    
    // Create the result element
    const resultElementName = hasMemberBoardTags ? 
      "GetMemberBoardCertificationsByBoardResult" : 
      "GetExamProviderCertificationsByBoardResult";
    const resultElement = xml.createElement(resultElementName);
    root.appendChild(resultElement);
    
    if (hasMemberBoardTags) {
      // Extract member board blocks using flexible namespace matching
      const boardRegex = /<(?:\w+:)?BoardMemberBoardWrapper[^>]*>[\s\S]*?<\/(?:\w+:)?BoardMemberBoardWrapper>/g;
      const boardMatches = cleanXml.match(boardRegex) || [];
      
      console.log(`Found ${boardMatches.length} member board matches using regex`);
      
      boardMatches.forEach(boardXml => {
        const board = xml.createElement("BoardMemberBoardWrapper");
        resultElement.appendChild(board);
        
        // Extract fields with flexible namespace matching
        const extractField = (fieldName) => {
          const regex = new RegExp(`<(?:\\w+:)?${fieldName}[^>]*>(.*?)<\/(?:\\w+:)?${fieldName}>`, 's');
          const match = boardXml.match(regex);
          if (match && match[1]) {
            const field = xml.createElement(fieldName);
            field.textContent = match[1].trim();
            board.appendChild(field);
          }
        };
        
        // Extract all possible fields
        [
          'Acronym', 'Name', 'WebSite', 'Email', 'Address', 'City',
          'Country', 'Phone', 'PresidentName', 'ReferentName', 'Id'
        ].forEach(extractField);
        
        // Extract certification blocks with flexible namespace matching
        const certRegex = /<(?:\w+:)?BoardMemberBoardCertificationWrapper[^>]*>[\s\S]*?<\/(?:\w+:)?BoardMemberBoardCertificationWrapper>/g;
        const certMatches = boardXml.match(certRegex) || [];
        
        certMatches.forEach(certXml => {
          const cert = xml.createElement("BoardMemberBoardCertificationWrapper");
          board.appendChild(cert);
          
          // Extract certification fields
          [
            'Id', 'Certification', 'StreamCertificationType', 'Languages',
            'ShortAcronymWithLevel', 'TypeOfCertifications', 'ViewOrder'
          ].forEach(fieldName => {
            const regex = new RegExp(`<(?:\\w+:)?${fieldName}[^>]*>(.*?)<\/(?:\\w+:)?${fieldName}>`, 's');
            const match = certXml.match(regex);
            if (match && match[1]) {
              const field = xml.createElement(fieldName);
              field.textContent = match[1].trim();
              cert.appendChild(field);
            }
          });
        });
      });
      
      return xml;
    }
    
    throw new Error('Unsupported response type');
  } catch (error) {
    console.error('Fallback XML parsing failed:', error);
    throw error;
  }
}

// Function to parse XML and extract exam provider data
function parseExamProviderData(input) {
  try {
    // Check if input is already a provider array (returned from extractProviderDataWithRegex)
    if (Array.isArray(input)) {
      console.log("Received pre-parsed provider array with", input.length, "providers");
      
      // Already parsed data, just format it properly
      return {
        providers: input,
        countries: [...new Set(input.map(p => p.country).filter(Boolean))],
        languages: [...new Set(input.flatMap(p => 
          p.exams ? p.exams.flatMap(e => e.languages || []) : []
        ).filter(Boolean))],
        exams: input.flatMap(p => 
          p.exams ? p.exams.map(e => ({
            name: e.name || e.exam,
            type: e.streamExamType,
            languages: e.languages || []
          })) : []
        )
      };
    }
    
    // Handle string input (XML data)
    const xmlString = input;
    console.log("Raw XML string length:", xmlString.length);
    console.log("XML start:", xmlString.substring(0, 200));
    console.log("XML end:", xmlString.substring(xmlString.length - 200));

    // Check if the string actually contains XML
    if (!xmlString.includes('<')) {
      console.error("XML string doesn't contain any XML tags");
      return {
        providers: [],
        countries: [],
        languages: [],
        exams: []
      };
    }

    // Extract provider data using regex as a fallback
    const providers = extractProviderDataWithRegex(xmlString);
    console.log("Successfully extracted providers:", providers.length);
    
    // Create structured data to return
    return {
      providers: providers,
      countries: [...new Set(providers.map(p => p.country).filter(Boolean))],
      languages: [...new Set(providers.flatMap(p => 
        p.exams ? p.exams.flatMap(e => e.languages || []) : []
      ).filter(Boolean))],
      exams: providers.flatMap(p => 
        p.exams ? p.exams.map(e => ({
          name: e.name || e.exam,
          type: e.streamExamType,
          languages: e.languages || []
        })) : []
      )
    };
  } catch (error) {
    console.error("Failed to parse exam provider data:", error);
    return {
      providers: [],
      countries: [],
      languages: [],
      exams: []
    };
  }
}

// Function to extract provider data using regex
function extractProviderDataWithRegex(xmlString) {
  try {
    // Clean XML string minimally
    const cleanXml = xmlString
      .trim()
      .replace(/\\"/g, '"')
      .replace(/\\\//g, '/');

    // First, check if this is a member board response
    const isMemberBoardResponse = cleanXml.includes('BoardMemberBoardWrapper');
    console.log('Detected response type:', isMemberBoardResponse ? 'Member Board' : 'Certification Board');

    if (isMemberBoardResponse) {
      // Extract the member board result section
      const resultRegex = /<ns2:GetMemberBoardCertificationsByBoardResult>([\s\S]*?)<\/ns2:GetMemberBoardCertificationsByBoardResult>/;
      const resultMatch = cleanXml.match(resultRegex);
      
      if (!resultMatch) {
        console.log('Could not find member board result section');
        return [];
      }
      
      const resultContent = resultMatch[1];
      
      // Extract BoardMemberBoardWrapper elements
      const boardRegex = /<ns1:BoardMemberBoardWrapper>[\s\S]*?<\/ns1:BoardMemberBoardWrapper>/g;
      const boardMatches = resultContent.match(boardRegex) || [];
      
      console.log(`Found ${boardMatches.length} member board matches`);
      
      // Extract each board's data
      return boardMatches.map(boardXml => {
        // Helper function to extract field text
        const extractField = (xml, fieldName) => {
          // First try exact match with ns1: prefix
          const exactRegex = new RegExp(`<ns1:${fieldName}([^>]*)>([^<]*(?:(?!<\/ns1:${fieldName}>)<[^<]*)*)<\/ns1:${fieldName}>`, 's');
          const exactMatch = xml.match(exactRegex);
          
          if (exactMatch && exactMatch[2]) {
            // Check if field contains xsi:nil attribute
            if (exactMatch[1] && exactMatch[1].includes('xsi:nil="true"')) {
              return null;
            }
            return exactMatch[2].trim();
          }
          
          // Try alternative namespace prefixes if exact match failed
          const altPrefixes = ['a:', 'ns2:', ''];
          for (const prefix of altPrefixes) {
            const altRegex = new RegExp(`<${prefix}${fieldName}([^>]*)>([^<]*(?:(?!<\/${prefix}${fieldName}>)<[^<]*)*)<\/${prefix}${fieldName}>`, 's');
            const altMatch = xml.match(altRegex);
            
            if (altMatch && altMatch[2]) {
              // Check if field contains xsi:nil attribute
              if (altMatch[1] && altMatch[1].includes('xsi:nil="true"')) {
                return null;
              }
              return altMatch[2].trim();
            }
          }
          
          return '';
        };

        // Extract available certifications
        const certRegex = /<ns1:CertificationTrainingProviderCourseWrapper>[\s\S]*?<\/ns1:CertificationTrainingProviderCourseWrapper>/g;
        const certMatches = boardXml.match(certRegex) || [];
        
        const certifications = certMatches.map(certXml => {
          return {
            exam: extractField(certXml, 'Exam'),
            examLevel: extractField(certXml, 'ExamLevel'),
            id: extractField(certXml, 'Id'),
            sources: extractField(certXml, 'Sources'),
            streamExamType: extractField(certXml, 'StreamExamType')
          };
        });

        // Extract board fields
        const board = {
          acronym: extractField(boardXml, 'Acronym'),
          address: extractField(boardXml, 'Address'),
          boardName: extractField(boardXml, 'BoardName'),
          boardOfficialWebsite: extractField(boardXml, 'BoardOfficialWebsite'),
          contactPerson: extractField(boardXml, 'ContactPerson'),
          contactPersonTitle: extractField(boardXml, 'ContactPersonTitle'),
          countryRegion: extractField(boardXml, 'CountryRegion'),
          countriesOfOperation: extractField(boardXml, 'CountriesOfOperation'),
          email: extractField(boardXml, 'Email'),
          fax: extractField(boardXml, 'Fax'),
          foundingYear: extractField(boardXml, 'FoundingYear'),
          phone: extractField(boardXml, 'Phone'),
          supportedLanguages: extractField(boardXml, 'SupportedLanguages'),
          logo: extractField(boardXml, 'Logo'),
          availableCertifications: certifications
        };
        
        console.log('Extracted certification board fields:', {
          boardName: board.boardName,
          acronym: board.acronym
        });
        
        return board;
      });
    } else {
      // Original implementation for certification boards
      // Extract the result section first
      const resultRegex = /<ns2:GetMemberBoardCertificationsByBoardResult>([\s\S]*?)<\/ns2:GetMemberBoardCertificationsByBoardResult>/;
      const resultMatch = cleanXml.match(resultRegex);
      
      if (!resultMatch) {
        // Try alternative result tag
        const altResultRegex = /<GetExamProviderCertificationsByBoardResult>([\s\S]*?)<\/GetExamProviderCertificationsByBoardResult>/;
        const altMatch = cleanXml.match(altResultRegex);
        
        if (!altMatch) {
          console.log('Could not find result section');
          
          // Try to extract based on provider wrappers directly
          const directProviderRegex = /<a:BoardExamProviderWrapper>[\s\S]*?<\/a:BoardExamProviderWrapper>/g;
          const directProviders = cleanXml.match(directProviderRegex);
          
          if (directProviders && directProviders.length > 0) {
            console.log(`Found ${directProviders.length} direct provider matches`);
            
            return directProviders.map(providerXml => {
              const extractAField = (xml, fieldName) => {
                const fieldRegex = new RegExp(`<a:${fieldName}>([^<]*(?:(?!<\/a:${fieldName}>)<[^<]*)*)<\/a:${fieldName}>`, 's');
                const match = xml.match(fieldRegex);
                
                if (match && match[1]) {
                  return match[1].trim();
                }
                return '';
              };
              
              // Extract available exams
              const examRegex = /<a:BoardExamProviderExamWrapper>[\s\S]*?<\/a:BoardExamProviderExamWrapper>/g;
              const examMatches = providerXml.match(examRegex) || [];
              
              const exams = examMatches.map(examXml => {
                return {
                  name: extractAField(examXml, 'Exam'),
                  id: extractAField(examXml, 'Id'),
                  languages: extractAField(examXml, 'Languages')?.split(',').map(l => l.trim()) || [],
                  shortAcronym: extractAField(examXml, 'ShortAcronymWithLevel'),
                  streamExamType: extractAField(examXml, 'StreamExamType'),
                  typeOfExams: extractAField(examXml, 'TypeOfExams')?.split(',').map(t => t.trim()) || []
                };
              });
              
              // Extract provider fields
              const provider = {
                name: extractAField(providerXml, 'ExamProviderName'),
                boardName: extractAField(providerXml, 'ExamProviderBorderName'),
                acronym: extractAField(providerXml, 'ExamProvideBorderAcronym'),
                country: extractAField(providerXml, 'Country'),
                countryRegion: extractAField(providerXml, 'CountryRegion'),
                countriesOfOperation: extractAField(providerXml, 'CountriesOfOperation'),
                email: extractAField(providerXml, 'Email'),
                website: extractAField(providerXml, 'WebSite'),
                address: extractAField(providerXml, 'Address'),
                city: extractAField(providerXml, 'City'),
                phone: extractAField(providerXml, 'Phone'),
                referencePerson: extractAField(providerXml, 'ReferencePerson'),
                fax: extractAField(providerXml, 'Fax'),
                id: extractAField(providerXml, 'Id'),
                exams: exams
              };
              
              console.log('Extracted provider fields:', {
                name: provider.name,
                boardName: provider.boardName,
                country: provider.country,
                countryRegion: provider.countryRegion,
                countriesOfOperation: provider.countriesOfOperation ? provider.countriesOfOperation.substring(0, 50) + '...' : null
              });
              
              return provider;
            });
          }
          
          return [];
        }
        
        // Use the alternative result match
        const resultContent = altMatch[1];
        
        // Look for <a: prefixed tags
        const providerRegex = /<a:BoardExamProviderWrapper>[\s\S]*?<\/a:BoardExamProviderWrapper>/g;
        const providerMatches = resultContent.match(providerRegex) || [];
        
        console.log(`Found ${providerMatches.length} exam provider matches with <a: prefix`);
        
        if (providerMatches.length > 0) {
          return providerMatches.map(providerXml => {
            const extractAField = (xml, fieldName) => {
              const fieldRegex = new RegExp(`<a:${fieldName}>([^<]*(?:(?!<\/a:${fieldName}>)<[^<]*)*)<\/a:${fieldName}>`, 's');
              const match = xml.match(fieldRegex);
              
              if (match && match[1]) {
                return match[1].trim();
              }
              return '';
            };
            
            // Extract available exams
            const examRegex = /<a:BoardExamProviderExamWrapper>[\s\S]*?<\/a:BoardExamProviderExamWrapper>/g;
            const examMatches = providerXml.match(examRegex) || [];
            
            const exams = examMatches.map(examXml => {
              return {
                name: extractAField(examXml, 'Exam'),
                id: extractAField(examXml, 'Id'),
                languages: extractAField(examXml, 'Languages')?.split(',').map(l => l.trim()) || [],
                shortAcronym: extractAField(examXml, 'ShortAcronymWithLevel'),
                streamExamType: extractAField(examXml, 'StreamExamType'),
                typeOfExams: extractAField(examXml, 'TypeOfExams')?.split(',').map(t => t.trim()) || []
              };
            });
            
            // Extract provider fields
            const provider = {
              name: extractAField(providerXml, 'ExamProviderName'),
              boardName: extractAField(providerXml, 'ExamProviderBorderName'),
              acronym: extractAField(providerXml, 'ExamProvideBorderAcronym'),
              country: extractAField(providerXml, 'Country'),
              countryRegion: extractAField(providerXml, 'CountryRegion'),
              countriesOfOperation: extractAField(providerXml, 'CountriesOfOperation'),
              email: extractAField(providerXml, 'Email'),
              website: extractAField(providerXml, 'WebSite'),
              address: extractAField(providerXml, 'Address'),
              city: extractAField(providerXml, 'City'),
              phone: extractAField(providerXml, 'Phone'),
              referencePerson: extractAField(providerXml, 'ReferencePerson'),
              fax: extractAField(providerXml, 'Fax'),
              id: extractAField(providerXml, 'Id'),
              exams: exams
            };
            
            console.log('Extracted provider fields:', {
              name: provider.name,
              boardName: provider.boardName,
              country: provider.country,
              countryRegion: provider.countryRegion,
              countriesOfOperation: provider.countriesOfOperation ? provider.countriesOfOperation.substring(0, 50) + '...' : null
            });
            
            return provider;
          });
        }
      }
      
      const resultContent = resultMatch[1];
      
      // Extract CertificationBoardWrapper elements
      const boardRegex = /<ns1:CertificationBoardWrapper>[\s\S]*?<\/ns1:CertificationBoardWrapper>/g;
      const boardMatches = resultContent.match(boardRegex) || [];
      
      console.log(`Found ${boardMatches.length} certification board matches`);
      
      // Extract each board's data
      return boardMatches.map(boardXml => {
        // Helper function to extract field text
        const extractField = (xml, fieldName) => {
          // First try exact match with ns1: prefix
          const exactRegex = new RegExp(`<ns1:${fieldName}([^>]*)>([^<]*(?:(?!<\/ns1:${fieldName}>)<[^<]*)*)<\/ns1:${fieldName}>`, 's');
          const exactMatch = xml.match(exactRegex);
          
          if (exactMatch && exactMatch[2]) {
            // Check if field contains xsi:nil attribute
            if (exactMatch[1] && exactMatch[1].includes('xsi:nil="true"')) {
              return null;
            }
            return exactMatch[2].trim();
          }
          
          // Try alternative namespace prefixes if exact match failed
          const altPrefixes = ['a:', 'ns2:', ''];
          for (const prefix of altPrefixes) {
            const altRegex = new RegExp(`<${prefix}${fieldName}([^>]*)>([^<]*(?:(?!<\/${prefix}${fieldName}>)<[^<]*)*)<\/${prefix}${fieldName}>`, 's');
            const altMatch = xml.match(altRegex);
            
            if (altMatch && altMatch[2]) {
              // Check if field contains xsi:nil attribute
              if (altMatch[1] && altMatch[1].includes('xsi:nil="true"')) {
                return null;
              }
              return altMatch[2].trim();
            }
          }
          
          return '';
        };

        // Extract available certifications
        const certRegex = /<ns1:CertificationTrainingProviderCourseWrapper>[\s\S]*?<\/ns1:CertificationTrainingProviderCourseWrapper>/g;
        const certMatches = boardXml.match(certRegex) || [];
        
        const certifications = certMatches.map(certXml => {
          return {
            exam: extractField(certXml, 'Exam'),
            examLevel: extractField(certXml, 'ExamLevel'),
            id: extractField(certXml, 'Id'),
            sources: extractField(certXml, 'Sources'),
            streamExamType: extractField(certXml, 'StreamExamType')
          };
        });

        // Extract board fields
        const board = {
          acronym: extractField(boardXml, 'Acronym'),
          address: extractField(boardXml, 'Address'),
          boardName: extractField(boardXml, 'BoardName'),
          boardOfficialWebsite: extractField(boardXml, 'BoardOfficialWebsite'),
          contactPerson: extractField(boardXml, 'ContactPerson'),
          contactPersonTitle: extractField(boardXml, 'ContactPersonTitle'),
          countryRegion: extractField(boardXml, 'CountryRegion'),
          countriesOfOperation: extractField(boardXml, 'CountriesOfOperation'),
          email: extractField(boardXml, 'Email'),
          fax: extractField(boardXml, 'Fax'),
          foundingYear: extractField(boardXml, 'FoundingYear'),
          phone: extractField(boardXml, 'Phone'),
          supportedLanguages: extractField(boardXml, 'SupportedLanguages'),
          logo: extractField(boardXml, 'Logo'),
          availableCertifications: certifications
        };
        
        console.log('Extracted certification board fields:', {
          boardName: board.boardName,
          acronym: board.acronym
        });
        
        return board;
      });
    }
  } catch (error) {
    console.error('Error extracting provider data:', error);
    return [];
  }
}

function useWindowSize() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 1000); // Switch to mobile view under 768px
    }

    // Set initial size
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
}

function OurComponent(props) {
  console.log('OurComponent mounting with props:', props);

  // Get isMobile from useWindowSize hook
  const isMobile = useWindowSize();

  // State declarations
  const [useApi, setUseApi] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [examProviderSearch, setExamProviderSearch] = useState("");
  const [countryOpen, setCountryOpen] = useState(false);
  const [examProviderOpen, setExamProviderOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [examProviders, setExamProviders] = useState([]);
  const [countries, setCountries] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [lastWorkingData, setLastWorkingData] = useState({
    providers: [],
    countries: [],
    languages: [],
    exams: []
  });
  const [dataType, setDataType] = useState("memberBoard"); // Default to member board view
  const [isTestingApi, setIsTestingApi] = useState(false);
  const [bypassCache, setBypassCache] = useState(false); // Development only: bypass cache
  const [showDevTools, setShowDevTools] = useState(false); // Hidden dev tools toggle

  // Refs
  const countryRef = useRef(null);
  const examProviderRef = useRef(null);
  
  // Filter arrays based on search inputs
  const filteredCountries = countries
    .filter(country => country && country.toLowerCase().includes(countrySearch.toLowerCase()))
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

  const filteredExamProviders = examProviders
    .filter(provider => {
    if (!provider) return false;
    
    const searchTerm = examProviderSearch.toLowerCase();
    return (
      (provider.name && provider.name.toLowerCase().includes(searchTerm)) ||
      (provider.BoardName && provider.BoardName.toLowerCase().includes(searchTerm)) ||
      (provider.boardName && provider.boardName.toLowerCase().includes(searchTerm)) ||
      (provider.Acronym && provider.Acronym.toLowerCase().includes(searchTerm)) ||
      (provider.acronym && provider.acronym.toLowerCase().includes(searchTerm))
    );
    })
    .sort((a, b) => {
      const nameA = (a.BoardName || a.boardName || a.name || a.Acronym || a.acronym || '').toLowerCase();
      const nameB = (b.BoardName || b.boardName || b.name || b.Acronym || b.acronym || '').toLowerCase();
      return nameA.localeCompare(nameB);
  });

  // Add test API function
  const testApi = async () => {
    try {
      setIsTestingApi(true);
      console.log('Testing API connection...');
      
      const soapEnvelope = `<?xml version="1.0" encoding="UTF-8"?>
<soap:Envelope xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">
  <soap:Body>
    <GetMemberBoardCertificationsByBoard xmlns="http://tempuri.org/">
      <userId>istqb</userId>
      <pwd>osh7Vae4aiku5queevae</pwd>
    </GetMemberBoardCertificationsByBoard>
  </soap:Body>
</soap:Envelope>`;

      console.log('Sending request to:', `${props.pluginUrl}/soap-proxy.php`);
      
      // Use our direct SOAP proxy instead of the REST API
      const soapProxyUrl = `${props.pluginUrl}/soap-proxy.php${bypassCache ? '?nocache=1' : ''}`;
      const response = await fetch(soapProxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/xml;charset=UTF-8',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: soapEnvelope
      });

      console.log('API Response Status:', response.status);
      console.log('API Response Headers:', Object.fromEntries(response.headers));

      const rawResponse = await response.text();
      console.log('Raw API Response:', rawResponse);
      console.log('Response length:', rawResponse.length);

      // Try to parse the response
      const providers = extractProviderDataWithRegex(rawResponse);
      console.log('Parsed Providers:', providers);

      if (response.ok) {
        // Update the state with the providers
        if (providers && providers.length > 0) {
          const parsedData = parseExamProviderData(providers);
          setExamProviders(parsedData.providers);
          setCountries(parsedData.countries);
          setLastWorkingData(parsedData);
          setDataType("memberBoard");
        }
        
        alert(`API Test Complete!\nStatus: ${response.status}\nProviders Found: ${providers.length}\nCheck console for details.`);
      } else {
        alert(`API Test Completed with errors!\nStatus: ${response.status}\nResponse: ${rawResponse.substring(0, 100)}...\nCheck console for details.`);
      }

    } catch (error) {
      console.error('API Test Error:', error);
      alert(`API Test Failed!\nError: ${error.message}\nCheck console for details.`);
    } finally {
      setIsTestingApi(false);
    }
  };

  // Define loadData function outside useEffect so it can be reused
  const loadData = async () => {
    try {
      if (useApi) {
        setIsSearchLoading(true);
        // Fetch member board data from API
        try {
          const memberBoardData = await fetchExamData(props.pluginUrl, bypassCache);
          if (memberBoardData) {
            try {
              // Check if we got an array of providers or XML string
              const parsedData = Array.isArray(memberBoardData) 
                ? parseExamProviderData(memberBoardData)
                : parseExamProviderData(memberBoardData);
              
              // Only update state if parsing was successful and data is valid
              if (parsedData && parsedData.providers && parsedData.providers.length > 0) {
                // Fix any missing CountriesOfOperation
                const fixedProviders = fixCountriesOfOperation(parsedData.providers);
                
                // Sort providers alphabetically by name
                const sortedProviders = fixedProviders.sort((a, b) => {
                  const nameA = (a.BoardName || a.boardName || a.name || a.Acronym || a.acronym || '').toLowerCase();
                  const nameB = (b.BoardName || b.boardName || b.name || b.Acronym || b.acronym || '').toLowerCase();
                  return nameA.localeCompare(nameB);
                });
                
                // Ensure every provider has countriesOfOperation for display purposes
                sortedProviders.forEach(provider => {
                  if (!provider.countriesOfOperation || provider.countriesOfOperation === '') {
                    const acronym = provider.acronym || provider.Acronym;
                    const name = provider.name || provider.boardName || provider.BoardName;
                    console.log(`Provider ${acronym || name} still missing CountriesOfOperation after fixes`);
                  }
                });
                
                setExamProviders(sortedProviders);
                setSearchResults(sortedProviders); // Set initial search results
                setCountries(parsedData.countries.sort((a, b) => a.localeCompare(b))); // Sort countries
                // Store as last working data
                setLastWorkingData({...parsedData, providers: sortedProviders});
                
                // Set data type based on content
                setDataType("memberBoard");
                console.log("Loaded Member Board data with", sortedProviders.length, "providers");
              } else {
                console.log('Using last working data due to invalid parsed data');
                setExamProviders(lastWorkingData.providers);
                setSearchResults(lastWorkingData.providers);
                setCountries(lastWorkingData.countries);
              }
            } catch (parseError) {
              console.error('Error parsing data:', parseError);
              // Fallback to last working data
              console.log('Using last working data due to parse error');
              setExamProviders(lastWorkingData.providers);
              setSearchResults(lastWorkingData.providers);
              setCountries(lastWorkingData.countries);
            }
          }
        } catch (fetchError) {
          console.error('Error fetching data:', fetchError);
          // Fallback to last working data
          setExamProviders(lastWorkingData.providers);
          setSearchResults(lastWorkingData.providers);
          setCountries(lastWorkingData.countries);
        } finally {
          setIsSearchLoading(false);
        }
      } else {
        // Local XML file handling...
        try {
          const response = await fetch(`${props.pluginUrl}/public/memberboard.xml`);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const xmlData = await response.text();
          try {
            const parsedData = parseExamProviderData(xmlData);
            if (parsedData && parsedData.providers && parsedData.providers.length > 0) {
              // Sort providers alphabetically
              const sortedProviders = parsedData.providers.sort((a, b) => {
                const nameA = (a.BoardName || a.boardName || a.name || a.Acronym || a.acronym || '').toLowerCase();
                const nameB = (b.BoardName || b.boardName || b.name || b.Acronym || b.acronym || '').toLowerCase();
                return nameA.localeCompare(nameB);
              });
              
              setExamProviders(sortedProviders);
              setSearchResults(sortedProviders); // Set initial search results
              setCountries(parsedData.countries.sort((a, b) => a.localeCompare(b))); // Sort countries
              setLastWorkingData({...parsedData, providers: sortedProviders});
            } else {
              console.log('Using last working data due to invalid local XML data');
              setExamProviders(lastWorkingData.providers);
              setSearchResults(lastWorkingData.providers);
              setCountries(lastWorkingData.countries);
            }
          } catch (parseError) {
            console.error('Error parsing local XML:', parseError);
            setExamProviders(lastWorkingData.providers);
            setSearchResults(lastWorkingData.providers);
            setCountries(lastWorkingData.countries);
          }
        } catch (error) {
          console.error('Error loading XML:', error);
          setExamProviders(lastWorkingData.providers);
          setSearchResults(lastWorkingData.providers);
          setCountries(lastWorkingData.countries);
        }
      }
    } catch (error) {
      console.error('Error in loadData:', error);
      // Fallback to last working data
      setExamProviders(lastWorkingData.providers);
      setSearchResults(lastWorkingData.providers);
      setCountries(lastWorkingData.countries);
      setIsSearchLoading(false);
    }
  };

  // Load data when component mounts
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useApi, props.pluginUrl]);

  // Reload data when bypassCache changes in development mode
  useEffect(() => {
    if ((isDevelopment() || showDevTools) && useApi) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bypassCache]);

  // Effect to update countries list from providers
  useEffect(() => {
    if (examProviders.length > 0) {
      // Extract all unique countries from CountriesOfOperation and CountryRegion
      const allCountries = examProviders.flatMap(provider => {
        let countries = [];
        
        // Get countries from CountriesOfOperation
        if (provider.countriesOfOperation) {
          const operationCountries = provider.countriesOfOperation
            .split(',')
            .map(country => country.trim())
            .filter(Boolean);
          countries.push(...operationCountries);
        }

        // Add CountryRegion if it exists and is not already included
        if (provider.countryRegion && !countries.includes(provider.countryRegion)) {
          countries.push(provider.countryRegion);
        }
        
        return countries;
      });
      
      // Filter out duplicates, empty values, and null values
      const uniqueCountries = [...new Set(allCountries.filter(Boolean))];
      // Sort countries alphabetically
      setCountries(uniqueCountries.sort((a, b) => a.localeCompare(b)));
      
      console.log("Updated countries list with", uniqueCountries.length, "unique countries");
    }
  }, [examProviders]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryRef.current && !countryRef.current.contains(event.target)) {
        setCountryOpen(false)
      }
      if (examProviderRef.current && !examProviderRef.current.contains(event.target)) {
        setExamProviderOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const clearAllSearches = () => {
    setCountrySearch("")
    setExamProviderSearch("")
    setCountryOpen(false)
    setExamProviderOpen(false)
  }

  const handleSearch = async () => {
    setIsSearchLoading(true);
    
    try {
      // If we don't have data yet, fetch it
      if (examProviders.length === 0 || countries.length === 0) {
        await loadData();
      }
      
      // Filter providers based on search criteria
      const results = examProviders.filter(provider => {
        const matchesProvider = !examProviderSearch || 
          (provider && (
            (provider.name && provider.name.toLowerCase().includes(examProviderSearch.toLowerCase())) ||
            (provider.boardName && provider.boardName.toLowerCase().includes(examProviderSearch.toLowerCase())) ||
            (provider.BoardName && provider.BoardName.toLowerCase().includes(examProviderSearch.toLowerCase()))
          ));
        
        // Check only CountriesOfOperation for country matches
        const providerCountries = [];
        
        if (provider.countriesOfOperation) {
          providerCountries.push(...provider.countriesOfOperation.split(',').map(country => country.trim()));
        }
        
        const matchesCountry = !countrySearch || 
          providerCountries.some(country => 
            country.toLowerCase().includes(countrySearch.toLowerCase())
          );
        
        return matchesProvider && matchesCountry;
      });

      // Sort results alphabetically
      const sortedResults = results.sort((a, b) => {
        const nameA = (a.BoardName || a.boardName || a.name || a.Acronym || a.acronym || '').toLowerCase();
        const nameB = (b.BoardName || b.boardName || b.name || b.Acronym || b.acronym || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setSearchResults(sortedResults);
    } catch (error) {
      console.error('Error during search:', error);
    } finally {
      setIsSearchLoading(false);
    }
  };

  // Add a function to handle clicking View Details
  const handleViewDetails = (provider) => {
    // Special case for ANZTB
    if ((provider.acronym === 'ANZTB' || 
         provider.boardName === 'Australia and New Zealand Testing Board' ||
         (provider.name && provider.name.includes('Australia and New Zealand'))) && 
        (!provider.countriesOfOperation || provider.countriesOfOperation === '')) {
      
      console.log('ANZTB detected in handleViewDetails, setting CountriesOfOperation');
      
      // Use cached value or hardcoded value
      provider.countriesOfOperation = window.anztbCountriesOfOperation || 'Australia,New Zealand';
      
      // Store this updated provider for later reference
      window.anztbProvider = provider;
    }
    
    // Log relevant provider data for debugging
    console.log('Provider details:', {
      name: provider.boardName || provider.BoardName || provider.name,
      acronym: provider.acronym || provider.Acronym,
      countriesOfOperation: provider.countriesOfOperation,
      country: provider.country,
      countryRegion: provider.countryRegion || provider.CountryRegion
    });
    
    // Set the selected provider and show modal
    setSelectedProvider({...provider}); // Create a copy to avoid reference issues
    setShowModal(true);
  };

  // Mobile Result Card
  const MobileResultCard = ({ provider }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
      {/* Board logo - always show, use ISTQB logo as placeholder */}
      <div className="flex justify-center mb-4">
        <img 
          src={provider?.Logo || provider?.logo || `${props.pluginUrl}/public/istqb-logo.png`}
          alt={`${provider?.BoardName || provider?.name || 'Board'} Logo`}
          className="h-16 object-contain"
        />
      </div>
      
      <div className="flex justify-between items-start mb-3">
        <h3 className="text-lg font-medium text-gray-900">{provider?.BoardName || provider?.boardName || provider?.name || 'Unnamed Board'}</h3>
        {(provider?.BoardOfficialWebsite || provider?.boardOfficialWebsite || provider?.website) && (
          <a
            href={((provider.BoardOfficialWebsite || provider.boardOfficialWebsite || provider.website).startsWith('http') 
              ? (provider.BoardOfficialWebsite || provider.boardOfficialWebsite || provider.website) 
              : `https://${provider.BoardOfficialWebsite || provider.boardOfficialWebsite || provider.website}`)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-custom-red hover:bg-custom-red-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-red"
          >
            Website
          </a>
        )}
      </div>
      
      <div className="space-y-2 text-sm text-gray-600 flex-grow">
        <p><span className="font-medium">Countries of Operation:</span> {
          provider.countriesOfOperation ? 
            provider.countriesOfOperation
              .split(',')
              .map(country => country.trim())
              .filter(Boolean)
              .join(', ') : 
            'Not specified'
        }</p>
        {(provider?.Email || provider?.email) && (
          <p><span className="font-medium">Email:</span> 
            <a 
              href={`mailto:${provider.Email || provider.email}`}
              className="ml-1 text-blue-600 hover:underline"
            >
              {provider.Email || provider.email}
            </a>
          </p>
        )}
        {(provider?.Acronym || provider?.acronym) && (
          <p><span className="font-medium">Acronym:</span> {provider.Acronym || provider.acronym}</p>
        )}
        {(provider?.FoundingYear || provider?.foundingYear) && (
          <p><span className="font-medium">Founded:</span> {provider.FoundingYear || provider.foundingYear}</p>
        )}
        {(provider?.SupportedLanguages || provider?.supportedLanguages) && (
          <p><span className="font-medium">Languages:</span> {provider.SupportedLanguages || provider.supportedLanguages}</p>
        )}
        {(provider?.ContactPerson || provider?.contactPerson) && (
          <p><span className="font-medium">Contact:</span> {provider.ContactPerson || provider.contactPerson}
            {(provider.ContactPersonTitle || provider.contactPersonTitle) ? `, ${provider.ContactPersonTitle || provider.contactPersonTitle}` : ''}
          </p>
        )}

      </div>
      
      <div className="mt-4 flex justify-end border-t pt-4">
        <button
          onClick={() => handleViewDetails(provider)}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          View Details
        </button>
      </div>
    </div>
  );

  // Add manual fix button in development mode (hidden in production)
  const renderDevTools = () => {
    return null; // Debug menu disabled
  };

  // After setting the results, add this debug code
  useEffect(() => {
    if (searchResults.length > 0) {
      console.debug('First search result:', searchResults[0]);
      console.debug('Countries of operation:', searchResults[0].countriesOfOperation);
    }
  }, [searchResults]);

  return (
    <div className="pim-istqb-plugin-wrapper" style={{ width: '100vw', maxWidth: '100%', margin: '0', padding: '0 20px' }}>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          {dataType === "memberBoard" ? "Find ISTQB® Member Boards" : "Find ISTQB® Exam Providers"}
        </h1>
      </div>
      {renderDevTools()}
      
      <br />
      
      {/* Desktop Dropdowns */}
      {!isMobile && (
        <div id="pim-desktop-dropdowns" className="p-6 rounded-lg mb-6">
          {/* Filters row */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Country Search */}
            <div className="relative" ref={countryRef}>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search for country..."
                  value={countrySearch}
                  onChange={(e) => {
                    setCountrySearch(e.target.value)
                    setCountryOpen(true)
                  }}
                  onFocus={() => setCountryOpen(true)}
                />
                <ClearButton 
                  show={countrySearch !== ""} 
                  onClick={(e) => {
                    e.stopPropagation()
                    setCountrySearch("")
                    setCountryOpen(false)
                  }} 
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  onClick={() => setCountryOpen(!countryOpen)}
                  title="Toggle dropdown"
                >
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${countryOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              
              {countryOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {countries.length === 0 ? (
                    <DropdownLoader />
                  ) : filteredCountries.map((country, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => {
                        setCountrySearch(country)
                        setCountryOpen(false)
                      }}
                    >
                      {country}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Exam Provider Search */}
            <div className="relative" ref={examProviderRef}>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search for member board..."
                  value={examProviderSearch}
                  onChange={(e) => {
                    setExamProviderSearch(e.target.value)
                    setExamProviderOpen(true)
                  }}
                  onFocus={() => setExamProviderOpen(true)}
                />
                <ClearButton 
                  show={examProviderSearch !== ""} 
                  onClick={(e) => {
                    e.stopPropagation()
                    setExamProviderSearch("")
                    setExamProviderOpen(false)
                  }} 
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  onClick={() => setExamProviderOpen(!examProviderOpen)}
                  title="Toggle dropdown"
                >
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${examProviderOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              
              {examProviderOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {examProviders.length === 0 ? (
                    <DropdownLoader />
                  ) : filteredExamProviders.map((provider, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => {
                        setExamProviderSearch(provider.BoardName || provider.boardName || provider.name || '')
                        setExamProviderOpen(false)
                      }}
                    >
                      {provider.BoardName || provider.boardName || provider.name || (provider.Acronym || provider.acronym || '')}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Search and Clear buttons row */}
          <div className="w-full flex items-center">
            <button
              onClick={handleSearch}
              style={{ 
                backgroundColor: '#c43e4b',
                color: 'white',
                padding: '0.5rem 1.5rem',
                borderRadius: '0.5rem',
                transition: 'background-color 200ms'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#a8363f'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#c43e4b'}
            >
              Search
            </button>

            <button
              onClick={clearAllSearches}
              className="ml-auto text-gray-600 hover:text-gray-800 px-6 py-2"
              style={{ float: 'right' }}
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Mobile Dropdowns */}
      {isMobile && (
        <div id="pim-mobile-dropdowns" className="p-6 rounded-lg mb-6">
          <div className="flex flex-col space-y-4">
            {/* Country Search */}
            <div className="relative" ref={countryRef}>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search for country..."
                  value={countrySearch}
                  onChange={(e) => {
                    setCountrySearch(e.target.value)
                    setCountryOpen(true)
                  }}
                  onFocus={() => setCountryOpen(true)}
                />
                <ClearButton 
                  show={countrySearch !== ""} 
                  onClick={(e) => {
                    e.stopPropagation()
                    setCountrySearch("")
                    setCountryOpen(false)
                  }} 
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  onClick={() => setCountryOpen(!countryOpen)}
                  title="Toggle dropdown"
                >
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${countryOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              {countryOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {countries.length === 0 ? (
                    <DropdownLoader />
                  ) : filteredCountries.map((country, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => {
                        setCountrySearch(country)
                        setCountryOpen(false)
                      }}
                    >
                      {country}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Exam Provider Search */}
            <div className="relative" ref={examProviderRef}>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search for exam provider..."
                  value={examProviderSearch}
                  onChange={(e) => {
                    setExamProviderSearch(e.target.value)
                    setExamProviderOpen(true)
                  }}
                  onFocus={() => setExamProviderOpen(true)}
                />
                <ClearButton 
                  show={examProviderSearch !== ""} 
                  onClick={(e) => {
                    e.stopPropagation()
                    setExamProviderSearch("")
                    setExamProviderOpen(false)
                  }} 
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  onClick={() => setExamProviderOpen(!examProviderOpen)}
                  title="Toggle dropdown"
                >
                  <svg 
                    className={`w-5 h-5 transition-transform duration-200 ${examProviderOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              {examProviderOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {examProviders.length === 0 ? (
                    <DropdownLoader />
                  ) : filteredExamProviders.map((provider, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => {
                        setExamProviderSearch(provider.BoardName || provider.boardName || provider.name || '')
                        setExamProviderOpen(false)
                      }}
                    >
                      {provider.BoardName || provider.boardName || provider.name || (provider.Acronym || provider.acronym || '')}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Search and Clear buttons row */}
            <div className="w-full flex items-center">
              <button
                onClick={handleSearch}
                style={{ 
                  backgroundColor: '#c43e4b',
                  color: 'white',
                  padding: '0.5rem 1.5rem',
                  borderRadius: '0.5rem',
                  transition: 'background-color 200ms'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#a8363f'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#c43e4b'}
              >
                Search
              </button>

              <button
                onClick={clearAllSearches}
                className="ml-auto text-gray-600 hover:text-gray-800 px-6 py-2"
                style={{ float: 'right' }}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search Results */}
      {isSearchLoading ? (
        <Loader />
      ) : searchResults.length > 0 ? (
        <>
          <style dangerouslySetInnerHTML={{
            __html: `
              .results-grid {
                display: grid;
                grid-template-columns: 1fr;
                gap: 1rem;
                margin-top: 1.5rem;
              }
              
              @media (min-width: 640px) {
                .results-grid {
                  grid-template-columns: repeat(2, 1fr);
                }
              }
              
              @media (min-width: 1024px) {
                .results-grid {
                  grid-template-columns: repeat(3, 1fr);
                }
              }
            `
          }} />
          
          <div className="results-grid">
            {searchResults.map((provider, index) => (
              isMobile ? 
              <MobileResultCard key={index} provider={provider} /> :
              <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-col">
                {/* Board logo - always show, use ISTQB logo as placeholder */}
                <div className="flex justify-center mb-4">
                  <img 
                    src={provider?.Logo || provider?.logo || `${props.pluginUrl}/public/istqb-logo.png`}
                    alt={`${provider?.BoardName || provider?.name || 'Board'} Logo`}
                    className="h-16 object-contain"
                  />
                </div>
                
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-medium text-gray-900">{provider?.BoardName || provider?.boardName || provider?.name || 'Unnamed Board'}</h3>
                  {(provider?.BoardOfficialWebsite || provider?.boardOfficialWebsite || provider?.website) && (
                    <a
                      href={((provider.BoardOfficialWebsite || provider.boardOfficialWebsite || provider.website).startsWith('http') 
                        ? (provider.BoardOfficialWebsite || provider.boardOfficialWebsite || provider.website) 
                        : `https://${provider.BoardOfficialWebsite || provider.boardOfficialWebsite || provider.website}`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-custom-red hover:bg-custom-red-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-red"
                    >
                      Website
                    </a>
                  )}
                </div>
                
                <div className="space-y-2 text-sm text-gray-600 flex-grow">
                  <p><span className="font-medium">Countries of Operation:</span> {
                    provider.countriesOfOperation ? 
                      provider.countriesOfOperation
                        .split(',')
                        .map(country => country.trim())
                        .filter(Boolean)
                        .join(', ') : 
                      'Not specified'
                  }</p>
                  {(provider?.Email || provider?.email) && (
                    <p><span className="font-medium">Email:</span> 
                      <a 
                        href={`mailto:${provider.Email || provider.email}`}
                        className="ml-1 text-blue-600 hover:underline"
                      >
                        {provider.Email || provider.email}
                      </a>
                    </p>
                  )}
                  {(provider?.Acronym || provider?.acronym) && (
                    <p><span className="font-medium">Acronym:</span> {provider.Acronym || provider.acronym}</p>
                  )}
                  {(provider?.FoundingYear || provider?.foundingYear) && (
                    <p><span className="font-medium">Founded:</span> {provider.FoundingYear || provider.foundingYear}</p>
                  )}
                  {(provider?.SupportedLanguages || provider?.supportedLanguages) && (
                    <p><span className="font-medium">Languages:</span> {provider.SupportedLanguages || provider.supportedLanguages}</p>
                  )}
                  {(provider?.ContactPerson || provider?.contactPerson) && (
                    <p><span className="font-medium">Contact:</span> {provider.ContactPerson || provider.contactPerson}
                      {(provider.ContactPersonTitle || provider.contactPersonTitle) ? `, ${provider.ContactPersonTitle || provider.contactPersonTitle}` : ''}
                    </p>
                  )}

                </div>
                
                <div className="mt-4 flex justify-end border-t pt-4">
                  <button
                    onClick={() => handleViewDetails(provider)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {/* Modal */}
      {showModal && selectedProvider && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 h-full w-full z-50 p-4">
          <div className="relative top-20 mx-auto border w-full max-w-2xl shadow-lg rounded-md bg-white">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white p-4 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-semibold" style={{ color: '#215184' }}>
                  {selectedProvider?.BoardName || selectedProvider?.name || selectedProvider?.Acronym || selectedProvider?.acronym || 'Board Details'}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-4 max-h-[calc(100vh-240px)] overflow-y-auto">
              <div className="text-sm">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  {/* Board logo - always show, use ISTQB logo as placeholder */}
                  <div className="flex justify-center mb-4">
                    <img 
                      src={selectedProvider?.Logo || selectedProvider?.logo || `${props.pluginUrl}/public/istqb-logo.png`}
                      alt={`${selectedProvider?.BoardName || selectedProvider?.name || 'Board'} Logo`}
                      className="h-20 object-contain" 
                    />
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="font-semibold mb-2 text-gray-700">
                      Member Board Details
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-gray-600">
                      {(selectedProvider?.BoardName || selectedProvider?.name || selectedProvider?.boardName) && (
                        <p className="col-span-2"><span className="font-medium">Name:</span> {selectedProvider.BoardName || selectedProvider.boardName || selectedProvider.name}</p>
                      )}
                      {(selectedProvider?.Acronym || selectedProvider?.acronym) && (
                        <p><span className="font-medium">Acronym:</span> {selectedProvider.Acronym || selectedProvider.acronym}</p>
                      )}
                      {(selectedProvider?.FoundingYear || selectedProvider?.foundingYear) && (
                        <p><span className="font-medium">Founded:</span> {selectedProvider.FoundingYear || selectedProvider.foundingYear}</p>
                      )}
                      {(selectedProvider?.ContactPerson || selectedProvider?.contactPerson) && (
                        <p className="col-span-2"><span className="font-medium">Contact Person:</span> {selectedProvider.ContactPerson || selectedProvider.contactPerson}</p>
                      )}
                      {(selectedProvider?.ContactPersonTitle || selectedProvider?.contactPersonTitle) && (
                        <p><span className="font-medium">Title:</span> {selectedProvider.ContactPersonTitle || selectedProvider.contactPersonTitle}</p>
                      )}
                      {(selectedProvider?.Address || selectedProvider?.address) && (
                        <p className="col-span-2"><span className="font-medium">Address:</span> {selectedProvider.Address || selectedProvider.address}</p>
                      )}
                      {(selectedProvider?.City || selectedProvider?.city) && (
                        <p><span className="font-medium">City:</span> {selectedProvider.City || selectedProvider.city}</p>
                      )}
                      {(selectedProvider?.CountryRegion || selectedProvider?.country || selectedProvider?.countryRegion) && (
                        <p><span className="font-medium">Headquarters:</span> {selectedProvider.CountryRegion || selectedProvider.countryRegion || selectedProvider.country}</p>
                      )}

                      {selectedProvider?.Fax !== undefined && selectedProvider.Fax !== null && (
                        <p><span className="font-medium">Fax:</span> {selectedProvider.Fax || selectedProvider.fax || 'Not provided'}</p>
                      )}
                      {(selectedProvider?.Email || selectedProvider?.email) && (
                        <p><span className="font-medium">Email:</span> 
                          <a 
                            href={`mailto:${selectedProvider.Email || selectedProvider.email}`}
                            className="ml-1 text-blue-600 hover:underline"
                          >
                            {selectedProvider.Email || selectedProvider.email}
                          </a>
                        </p>
                      )}
                      {(selectedProvider?.BoardOfficialWebsite || selectedProvider?.website || selectedProvider?.boardOfficialWebsite) && (
                        <p className="col-span-2"><span className="font-medium">Website:</span> 
                          <a 
                            href={(selectedProvider.BoardOfficialWebsite || selectedProvider.boardOfficialWebsite || selectedProvider.website).startsWith('http') 
                              ? (selectedProvider.BoardOfficialWebsite || selectedProvider.boardOfficialWebsite || selectedProvider.website) 
                              : `https://${selectedProvider.BoardOfficialWebsite || selectedProvider.boardOfficialWebsite || selectedProvider.website}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="ml-1 text-blue-600 hover:underline"
                          >
                            {selectedProvider.BoardOfficialWebsite || selectedProvider.boardOfficialWebsite || selectedProvider.website}
                          </a>
                        </p>
                      )}
                      {(selectedProvider?.SupportedLanguages || selectedProvider?.supportedLanguages) && (
                        <p><span className="font-medium">Languages:</span> {selectedProvider.SupportedLanguages || selectedProvider.supportedLanguages}</p>
                      )}
                      <p className="col-span-2"><span className="font-medium">Countries of Operation:</span> {
                        selectedProvider.countriesOfOperation ? 
                          selectedProvider.countriesOfOperation
                            .split(',')
                            .map(country => country.trim())
                            .filter(Boolean)
                            .join(', ') : 
                          'Not specified'
                      }</p>
                    </div>

                    {/* Available Certifications Section */}
                    {(selectedProvider?.AvailableIstqbCertifications || selectedProvider?.availableCertifications) && (
                      <div className="mt-4">
                        <h4 className="font-semibold mb-2 text-gray-700">
                          Available Certifications
                        </h4>
                        
                        <ul className="space-y-2">
                          {Array.isArray(selectedProvider.AvailableIstqbCertifications || selectedProvider.availableCertifications) ? (
                            (selectedProvider.AvailableIstqbCertifications || selectedProvider.availableCertifications).map((cert, index) => (
                              <li key={index} className="border-b border-gray-100 pb-2">
                                <p className="font-medium">• {cert?.Name || cert?.Certification || cert?.name || cert?.certification || cert?.exam || 'Unnamed Certification'}</p>
                                <div className="text-sm text-gray-600 ml-4">
                                  {(cert?.StreamCertificationType || cert?.streamCertificationType || cert?.streamExamType) && (
                                    <p>Type: {cert.StreamCertificationType || cert.streamCertificationType || cert.streamExamType}</p>
                                  )}
                                  {(cert?.Languages || cert?.languages) && (
                                    <p>Languages: {typeof (cert.Languages || cert.languages) === 'string' 
                                      ? (cert.Languages || cert.languages) 
                                      : (cert.Languages || cert.languages).join(', ')}</p>
                                  )}
                                  {(cert?.TypeOfCertifications || cert?.typeOfCertifications || cert?.typeOfExams) && (
                                    <p>Certification Types: {typeof (cert.TypeOfCertifications || cert.typeOfCertifications || cert.typeOfExams) === 'string' 
                                      ? (cert.TypeOfCertifications || cert.typeOfCertifications || cert.typeOfExams) 
                                      : (cert.TypeOfCertifications || cert.typeOfCertifications || cert.typeOfExams).join(', ')}</p>
                                  )}
                                  {(cert?.ShortAcronymWithLevel || cert?.shortAcronymWithLevel || cert?.shortAcronym) && (
                                    <p>Acronym: {cert.ShortAcronymWithLevel || cert.shortAcronymWithLevel || cert.shortAcronym}</p>
                                  )}
                                </div>
                              </li>
                            ))
                          ) : (
                            <li className="text-gray-500">Certification information not available</li>
                          )}
                        </ul>
                      </div>
                    )}
                    
                    {/* Support for legacy exam format */}
                    {selectedProvider?.exams && Array.isArray(selectedProvider.exams) && selectedProvider.exams.length > 0 && (
                      <div className="mt-4">
                        <h4 className="font-semibold mb-2 text-gray-700">
                          Available Certifications
                        </h4>
                        
                        <ul className="space-y-2">
                          {selectedProvider.exams.map((exam, index) => (
                            <li key={index} className="border-b border-gray-100 pb-2">
                              <p className="font-medium">• {exam?.name || exam?.exam || 'Unnamed Certification'}</p>
                              <div className="text-sm text-gray-600 ml-4">
                                {(exam?.streamExamType || exam?.type) && (
                                  <p>Type: {exam.streamExamType || exam.type}</p>
                                )}
                                {exam?.languages && exam.languages.length > 0 && (
                                  <p>Languages: {exam.languages.join(', ')}</p>
                                )}
                                {exam?.typeOfExams && exam.typeOfExams.length > 0 && (
                                  <p>Certification Types: {exam.typeOfExams.join(', ')}</p>
                                )}
                                {exam?.shortAcronym && (
                                  <p>Acronym: {exam.shortAcronym}</p>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
